import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { FileText, Plus } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { ReportActionToolbar } from "../../components/reports/ReportActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import {
  ZReportTable,
  ZReportItem,
} from "../../components/reports/ZReportTable";
import { ReportDateRangePicker } from "../../components/reports/ReportDateRangePicker";
import {
  startOfDay,
  endOfDay,
  subMonths,
  startOfMonth,
  parse,
  format,
} from "date-fns";
import { useAuth } from "../../auth/AuthContext";
import { fetchZReports, generateZReport, resetZReport } from "../../services/reportsApi";
import { useToast } from "../../hooks/useToast";

const ZReport: React.FC = () => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const navigate = useNavigate();
  const toast = useToast();
  const [reports, setReports] = useState<ZReportItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });

  // Fetch Z Reports from API
  useEffect(() => {
    const loadReports = async () => {
      setIsLoading(true);
      try {
        const response = await fetchZReports();
        // Parse dates and add dateObj for filtering, format currency
        const reportsWithDates = response.reports.map((report) => ({
          ...report,
          dateObj: parse(report.date, "dd MMM yyyy hh:mm a", new Date()),
          total_sale_amount: `${currencySymbol}${report.total_sale_amount}`,
          products_total_amount: `${currencySymbol}${report.products_total_amount}`,
          services_total_amount: `${currencySymbol}${report.services_total_amount}`,
          total_tender_amount: `${currencySymbol}${report.total_tender_amount}`,
          total_quick_payments: `${currencySymbol}${report.total_quickPayments}`,
        }));
        setReports(reportsWithDates);
      } catch (error) {
        console.error("Failed to fetch Z reports:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadReports();
  }, [currencySymbol]);

  const refreshReportsList = () => {
    fetchZReports().then((response) => {
      const reportsWithDates = response.reports.map((report) => ({
        ...report,
        dateObj: parse(report.date, "dd MMM yyyy hh:mm a", new Date()),
        total_sale_amount: `${currencySymbol}${report.total_sale_amount}`,
        products_total_amount: `${currencySymbol}${report.products_total_amount}`,
        services_total_amount: `${currencySymbol}${report.services_total_amount}`,
        total_tender_amount: `${currencySymbol}${report.total_tender_amount}`,
        total_quick_payments: `${currencySymbol}${report.total_quickPayments}`,
      }));
      setReports(reportsWithDates);
    });
  };

  const filteredReports = reports.filter((r) => {
    const matchesSearch = r.date
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    let matchesDate = true;
    if (dateRange.start && dateRange.end && r.dateObj) {
      matchesDate =
        r.dateObj >= startOfDay(dateRange.start) &&
        r.dateObj <= endOfDay(dateRange.end);
    }
    return matchesSearch && matchesDate;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReports = filteredReports.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleView = (report: ZReportItem) => {
    navigate(`/reports/z-report/${report.id}`);
  };

  const handleGenerateReport = async () => {
    const start = dateRange.start ?? startOfDay(new Date());
    const end = dateRange.end ?? endOfDay(new Date());
    const startDate = format(start, "yyyy-MM-dd");
    const endDate = format(end, "yyyy-MM-dd");

    setIsGenerating(true);
    try {
      const result = await generateZReport({ startDate, endDate });
      toast.success(result.message || "Z-Report generated successfully!");

      // Refresh the reports list after successful generation
      refreshReportsList();
      setCurrentPage(1); // Reset to first page to show the new report
    } catch (error: any) {
      console.error("Failed to generate Z report:", error);
      const errorMessage = error.response?.data?.error || "Failed to generate Z report";
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleResetReportById = async (report: ZReportItem) => {
    setIsGenerating(true);
    try {
      const result = await resetZReport(report.id);
      toast.success(result.message || "Z-Report reset successfully!");
      refreshReportsList();
    } catch (error: any) {
      console.error("Failed to reset Z report:", error);
      const errorMessage = error.response?.data?.error || "Failed to reset Z report";
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden p-4">
        <SubPageHeader
          title="Z-Report"
          subtitle="View and generate daily Z-Reports"
          icon={FileText}
          actions={
            <div className="flex items-center gap-3">
              <ReportDateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={(start, end) => setDateRange({ start, end })}
              />
              <Button
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-4 py-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleGenerateReport}
                disabled={isGenerating}
                title="Generate Z Report"
              >
                {isGenerating ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    <span>Generate Z Report</span>
                  </>
                )}
              </Button>
            </div>
          }
        />

        <ReportActionToolbar
          totalItems={reports.length}
          showingStart={filteredReports.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredReports.length)}
          searchTerm={searchTerm}
          onSearchChange={(val) => {
            setSearchTerm(val);
            setCurrentPage(1);
          }}
          searchPlaceholder="Search reports..."
        />

        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <ZReportTable
            reports={currentReports}
            isLoading={isLoading}
            indexOfFirstItem={indexOfFirstItem}
            onView={handleView}
            onReset={handleResetReportById}
            resetLoading={isGenerating}
          />
          <PaginationFooter
            totalItems={reports.length}
            filteredTotal={filteredReports.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};
export default ZReport;
